import React, { useContext, useEffect, useState } from 'react';
import { createMsalClient, createMsalConfig } from '../../util/misc';
import {
    AccountManager,
    api,
    AppInsights,
    Util,
} from '@bluedolphin/util-module-mf';
import {
    AccountInfo,
    AuthenticationResult,
    AuthError,
    InteractionRequiredAuthError,
    PublicClientApplication,
    RedirectRequest,
    SilentRequest,
} from '@azure/msal-browser';
import { TrackingKeys } from '../../util/tracking-keys';
import { useTenant } from '../tenant';

/**
 * Claims object type
 */
type Claims = {
    [key: string]: string;
};

/**
 * Subset of properties from AuthenticationResult
 */
type AuthResult =
    | AuthenticationResult
    | Pick<AuthenticationResult, 'accessToken' | 'expiresOn' | 'state'>;

/**
 * Azure Msal Provider Context Type
 */
export type AzureContextValue = {
    getDeepLink: () => string;
    clearSessionTimeout: () => void;
    authenticationResult: AuthResult;
    login: (b2cPolicy: Record<string, any>) => void;
    sessionExpiredHandler: () => void;
};

/**
 * Azure Msal Provider Context Instance
 */
export const MsalContext = React.createContext<AzureContextValue>(null);

/**
 * Msal Context Provider
 *
 * @returns {JSX.Element}
 */
export function AzureMsalProvider({
    children,
    customProps: { singleSpa },
}): JSX.Element {
    const [instance, setInstance] = useState<PublicClientApplication>(null);
    const [loginRequest, setLoginRequest] = useState<Record<string, any>>(null);
    const [forceRefresh, setForceRefresh] = useState<boolean>(false);
    const [authenticationResult, setAuthenticationResult] =
        useState<AuthResult>(null);
    const [timeoutId, setTimeoutId] =
        useState<ReturnType<typeof setTimeout>>(null);
    const { tenantMeta: tenant } = useTenant();

    /**
     * If accessToken is valid then the user is validated and
     * redirect to current or landingPage/deepLink
     */
    useEffect(() => {
        if (tenant) {
            const { token: accessToken, p_name } = AccountManager.getTenant();
            if (accessToken && !Util.JWT.isExpired(accessToken)) {
                const expiresOn = new Date(Util.JWT.getExpiration(accessToken));
                api.setAuthorization(accessToken);
                api.setB2CAuthorization(p_name);
                sessionTimeoutHandler(accessToken, expiresOn);
                setAuthenticationResult({
                    accessToken,
                    expiresOn,
                    state: 'authenticated',
                });
            } else if (!authenticationResult) {
                setAuthenticationResult({
                    accessToken: null,
                    expiresOn: null,
                    state: 'no-authenticated',
                });
            }
        }
    }, [tenant]);

    /**
     * Initializes MSAL Client Application when new login '/#'
     */
    useEffect(() => {
        if (window.location.hash.length) {
            const account = AccountManager.getTenant();
            initPublicClientApplication(account);
            setLoginRequest(account);
        }
    }, []);

    /**
     * Redirect flow starts here
     */
    useEffect(() => {
        if (loginRequest) {
            (async function redirectFlow() {
                try {
                    const { inviteId, b2cPolicy } = loginRequest;
                    const response = await instance.handleRedirectPromise();
                    if (response) {
                        handleRedirectPromisseSuccess(response);
                    } else if (!inviteId && getAccount(b2cPolicy)) {
                        acquireTokenSilent();
                    } else {
                        loginRedirect();
                    }
                } catch (error) {
                    handleRedirectError(
                        TrackingKeys.AuthLoginHandleRedirectError,
                        error,
                        'auth_login_error'
                    );
                }
            })();
        }
    }, [loginRequest]);

    /**
     * Returns the account that match the condition or null
     *
     * @returns {AccountInfo | null}
     */
    function getAccount(b2cPolicy: string): AccountInfo | null {
        const target = instance.getAllAccounts().filter((account) => {
            if (account?.idTokenClaims) {
                const { tfp } = account.idTokenClaims as Claims;
                return (
                    b2cPolicy.includes(tfp) &&
                    b2cPolicy.includes(account.environment)
                );
            }
        });
        return target.length ? target[0] : null;
    }

    /**
     * Silently acquire an access token for a given set of scopes (fromCache)
     *
     * @param {boolean} forceRefresh If true, skips the cache and refreshes accessToken
     */
    async function acquireTokenSilent() {
        try {
            const cacheAccount = getAccount(loginRequest.b2cPolicy);

            const silentRequest: SilentRequest = {
                scopes: [`https://${loginRequest.b2cTenant}/appnew/read`],
                account: cacheAccount,
                forceRefresh,
            };

            AppInsights.trackEvent(TrackingKeys.AuthLoginTokenSilentStarted, {
                loginRequest,
            });

            const response = await instance.acquireTokenSilent(silentRequest);

            if (response) {
                if (response.accessToken.length === 0) {
                    AppInsights.trackEvent(TrackingKeys.AuthLoginEmptyToken, {
                        loginRequest,
                    });
                    throw new InteractionRequiredAuthError(
                        '0000',
                        'Empty token'
                    );
                } else {
                    handleRedirectPromisseSuccess(response);
                }
            }
        } catch (error) {
            AppInsights.trackEvent(TrackingKeys.AuthLoginTokenSilentError, {
                loginRequest,
                error,
            });

            if (error instanceof InteractionRequiredAuthError) {
                const { b2cTenant, name } = loginRequest;
                const redirectRequest: RedirectRequest = {
                    scopes: [`https://${b2cTenant}/appnew/read`],
                    state: name,
                };

                AppInsights.trackEvent(
                    TrackingKeys.AuthLoginTokenRedirectStarted,
                    { loginRequest }
                );

                instance
                    .acquireTokenRedirect(redirectRequest)
                    .catch((error) =>
                        handleRedirectError(
                            TrackingKeys.AuthLoginTokenRedirectError,
                            error
                        )
                    );
            } else {
                loginRedirect();
            }
        }
    }

    /**
     * Use when initiating the login process by redirecting the user's browser to the authorization endpoint.
     * This function redirects the page, so any code that follows this function will not execute.
     */
    function loginRedirect() {
        const key = 'msal.interaction.status';
        const { b2cTenant, name } = loginRequest;
        const redirectRequest: RedirectRequest = {
            scopes: [`https://${b2cTenant}/appnew/read`],
            state: name,
        };

        if (!window.sessionStorage.getItem(key) && Util.Cookie.get(key)) {
            Util.Cookie.remove(key);
        }

        AppInsights.trackEvent(TrackingKeys.AuthLoginRedirectStarted, {
            loginRequest,
        });

        instance.loginRedirect(redirectRequest).catch((error: AuthError) => {
            AppInsights.trackEvent(TrackingKeys.AuthLoginRedirectError, {
                loginRequest,
                error,
            });
        });
    }

    /**
     * Redirect promisse success handler
     */
    function handleRedirectPromisseSuccess(response: AuthenticationResult) {
        const { accessToken, expiresOn } = response;
        const { p_name } = AccountManager.getTenant();
        AppInsights.trackEvent(TrackingKeys.AuthLoginEnded, {});
        api.setAuthorization(accessToken);
        api.setB2CAuthorization(p_name);
        sessionTimeoutHandler(accessToken, expiresOn);
        setForceRefresh(false);
        setAuthenticationResult(response);
    }

    /**
     * Redirect promisse error handler
     *
     * @param {string} trackingKey
     * @param {AuthError} error
     * @param {string} message
     */
    function handleRedirectError(
        trackingKey: string,
        error: AuthError,
        message: string = 'default'
    ) {
        AppInsights.trackEvent(trackingKey, { loginRequest, error });
        AccountManager.clearMsalSession();
        AccountManager.setTenantName(loginRequest.name);
        Util.navigateWithError(loginRequest.rootURL, {
            name: 'Authentication',
            message,
        });
    }

    /**
     * Creates an instance of PublicClientApplication
     *
     * @param {Record<string, any>} config
     */
    function initPublicClientApplication(
        config: Record<string, any> = AccountManager.getTenant()
    ) {
        const { b2cPolicy, b2cTenant, b2cClientId } = config;
        if (b2cPolicy && b2cTenant && b2cClientId) {
            const config = createMsalConfig(b2cPolicy, b2cTenant, b2cClientId);
            const msalInstance = createMsalClient(config);
            setInstance(msalInstance);
        }
    }

    /**
     * Returns deep link url or undefined
     *
     * @returns {string | undefined}
     */
    function getDeepLink(): string | undefined {
        const { pathname, search } = window.location;
        const { name } = AccountManager.getTenant();
        if (!pathname.includes(`${name}/login`)) {
            const pathName = pathname.substring(1);
            return pathName.substring(pathName.indexOf('/') + 1) + search;
        }
    }

    /**
     * Sets deep link URL and redirects the user to forcelogin
     */
    function sessionExpiredHandler() {
        const { pathname } = window.location;
        const isGuestLink = !!singleSpa.pathToActiveWhen(
            '/:tenantId/guestlink'
        )(window.location);
        const { rootURL, name } = AccountManager.getTenant();

        AppInsights.trackEvent(TrackingKeys.AuthSessionExpired, {});

        if (!isGuestLink && !pathname.includes(`${name}/login`)) {
            const deepLinkURL = getDeepLink();
            AccountManager.setItem('deepLinkURL', deepLinkURL);
            window.history.pushState(
                {},
                '',
                `${rootURL}forcelogin?error=session_expired`
            );
        }
    }

    /**
     * Redirects to login page when the session is expired
     *
     * @param {string} accessToken
     * @param {Date} expiresOn
     */
    function sessionTimeoutHandler(accessToken: string, expiresOn: Date): void {
        if (accessToken && expiresOn) {
            const now = new Date();
            clearSessionTimeout();
            if (expiresOn > now) {
                const trigger = expiresOn.valueOf() - now.valueOf();
                const id = setTimeout(sessionExpiredHandler, trigger);
                setTimeoutId(id);
            }
        }
    }

    /**
     * Clear session expired timeout
     */
    function clearSessionTimeout() {
        clearTimeout(timeoutId);
    }

    /**
     * Find the b2c policy name of the policy name
     *
     * @param {String} b2cPolicy
     * @return {String} Policy Name
     */
    function getPolicyName(b2cPolicy: string): string {
        let policyName: string;
        const { b2CPolicies, b2CVendor } = tenant;

        for (const policy of b2CPolicies) {
            if (policy.b2cPolicy === b2cPolicy) {
                policyName = policy.b2cPolicyName;
                break;
            }
        }

        if (!policyName && b2CVendor) {
            policyName = b2CVendor.b2cPolicyName;
        }

        return policyName;
    }

    /**
     * Create the object account
     *
     * @param {Record} policy
     */
    function getTargetAccount(policy: Record<string, any>) {
        const { name } = tenant;
        const rootURL = `/${name}/`;
        const { b2cPolicy } = policy;
        const p_name = getPolicyName(b2cPolicy);
        const account = AccountManager.getTenant(name);

        if (account.b2cPolicy && account.b2cPolicy !== b2cPolicy) {
            AccountManager.clearMsalSession();
            AccountManager.setTenantName(name);
        }
        return { ...account, ...policy, p_name, rootURL, name };
    }

    /**
     * Logs in the user by using redirect flow
     * @param {Record} policy
     */
    function login(policy: Record<string, any>) {
        const account = getTargetAccount(policy);

        initPublicClientApplication(policy);

        AccountManager.setTenantName(account.name);
        AccountManager.setTenant(account.name, account);

        setLoginRequest(account);
    }

    return (
        <MsalContext.Provider
            value={{
                login,
                getDeepLink,
                sessionExpiredHandler,
                clearSessionTimeout,
                authenticationResult,
            }}
        >
            {children}
        </MsalContext.Provider>
    );
}

/**
 * A wrapper around the useContext Hook
 * Returns the msal context app
 *
 * @returns {MsalContext}
 */
export function useAzureMsal(): AzureContextValue {
    return useContext(MsalContext);
}
